// src/pages/ApplicationBaladaApp/components/BaladaAppTable/BaladaAppTable.tsx
import { Box, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { BaladaApp, colorByBaladaAppType, translateBaladaAppType } from '@/model';
import { useTranslation } from 'react-i18next';

export interface BaladaAppTableProps {
  data: BaladaApp[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: BaladaApp) => void;
  onDeleteClick: (item: BaladaApp) => void;
}

export const BaladaAppTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
}: BaladaAppTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<BaladaApp>();

  const columns = [
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_balada_app.components.balada_app_table.balada_app_table_created_at_header'),
    }),

    columnHelper.accessor('title', {
      cell: props => props.getValue(),
      header: t('pages.application_balada_app.components.balada_app_table.balada_app_table_title_header'),
    }),

    columnHelper.accessor('type', {
      cell: props => (
        <Text position='relative' paddingLeft='16px'>
          <Box
            as='span'
            position='absolute'
            top='50%'
            left='0'
            transform='translateY(-50%)'
            w='8px'
            h='8px'
            borderRadius='50%'
            bgColor={colorByBaladaAppType(props.getValue())}
            zIndex={9999}
            aria-hidden='true'
          />
          {translateBaladaAppType(props.getValue())}
        </Text>
      ),
      header: t('pages.application_balada_app.components.balada_app_table.balada_app_table_type_header'),
    }),

    columnHelper.display({
      id: 'actions',
      header: t('pages.application_balada_app.components.balada_app_table.balada_app_table_actions_header'),
      cell: props => (
        <Flex justifyContent={'end'}>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_balada_app.components.balada_app_table.balada_app_table_menu_aria_label')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick?.(props.row.original as BaladaApp)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_balada_app.components.balada_app_table.balada_app_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original as BaladaApp)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_balada_app.components.balada_app_table.balada_app_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
